import { useCallback, useState } from 'react';
import { Columns3, RotateCcw } from 'lucide-react';
import { Column } from 'components/display/Table/types';

/**
 * Personnalisation des colonnes d'un ServerTable : visibilité (sélecteur) et ordre
 * (drag & drop des en-têtes), persistées en cache navigateur (localStorage) par
 * tableau via une storageKey.
 */
export interface ColumnPrefs {
  /** Clés de colonnes masquées. */
  hidden: string[];
  /** Ordre des clés (les colonnes absentes gardent leur position naturelle). */
  order: string[];
}

const EMPTY_PREFS: ColumnPrefs = { hidden: [], order: [] };
const storageKeyFor = (key: string) => `table-columns:${key}`;

export function loadColumnPrefs(storageKey: string, defaultHidden: string[] = []): ColumnPrefs {
  const fallback: ColumnPrefs = { hidden: defaultHidden, order: [] };
  try {
    const raw = localStorage.getItem(storageKeyFor(storageKey));
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return {
      hidden: Array.isArray(parsed?.hidden) ? parsed.hidden : defaultHidden,
      order: Array.isArray(parsed?.order) ? parsed.order : []
    };
  } catch {
    return fallback;
  }
}

export function saveColumnPrefs(storageKey: string, prefs: ColumnPrefs): void {
  try {
    localStorage.setItem(storageKeyFor(storageKey), JSON.stringify(prefs));
  } catch {
    /* stockage indisponible : préférences non persistées, sans gravité */
  }
}

/** Hook d'état des préférences, synchronisé avec le localStorage (inactif sans clé). */
export function useColumnPrefs(storageKey?: string, defaultHidden?: string[]) {
  const [prefs, setPrefs] = useState<ColumnPrefs>(() =>
    storageKey ? loadColumnPrefs(storageKey, defaultHidden) : EMPTY_PREFS
  );

  const updatePrefs = useCallback(
    (next: ColumnPrefs) => {
      setPrefs(next);
      if (storageKey) saveColumnPrefs(storageKey, next);
    },
    [storageKey]
  );

  return { prefs, updatePrefs };
}

/**
 * Applique ordre + visibilité aux colonnes. Une colonne inconnue des préférences
 * (ex. nouvelle colonne livrée par le back) reste visible, à sa position naturelle.
 */
export function applyColumnPrefs<T extends Record<string, any>>(
  columns: Column<T>[],
  prefs: ColumnPrefs
): Column<T>[] {
  const position = (col: Column<T>) => {
    const idx = prefs.order.indexOf(col.key);
    return idx === -1 ? prefs.order.length + columns.indexOf(col) : idx;
  };
  return [...columns]
    .sort((a, b) => position(a) - position(b))
    .filter((col) => !prefs.hidden.includes(col.key));
}

/** Déplace fromKey juste avant toKey dans l'ordre (drag & drop d'en-têtes). */
export function moveColumnBefore<T extends Record<string, any>>(
  columns: Column<T>[],
  prefs: ColumnPrefs,
  fromKey: string,
  toKey: string
): ColumnPrefs {
  // Repart de l'ordre effectif courant (préférences appliquées, colonnes masquées incluses)
  const position = (col: Column<T>) => {
    const idx = prefs.order.indexOf(col.key);
    return idx === -1 ? prefs.order.length + columns.indexOf(col) : idx;
  };
  const currentOrder: string[] = [...columns]
    .sort((a, b) => position(a) - position(b))
    .map((c) => c.key as string);
  const without = currentOrder.filter((key) => key !== fromKey);
  const targetIndex = without.indexOf(toKey);
  if (targetIndex === -1) return prefs;
  without.splice(targetIndex, 0, fromKey);
  return { ...prefs, order: without };
}

interface ColumnPickerProps<T extends Record<string, any>> {
  /** Toutes les colonnes du tableau (ordre naturel), pour lister les choix. */
  columns: Column<T>[];
  prefs: ColumnPrefs;
  onChange: (prefs: ColumnPrefs) => void;
}

/**
 * Bouton « Colonnes » + panneau de sélection : cocher/décocher les colonnes affichées.
 * L'état est persisté par tableau (localStorage).
 */
export function ColumnPicker<T extends Record<string, any>>({
  columns,
  prefs,
  onChange
}: ColumnPickerProps<T>) {
  const [isOpen, setIsOpen] = useState(false);

  const toggleColumn = (key: string) => {
    const hidden = prefs.hidden.includes(key)
      ? prefs.hidden.filter((k) => k !== key)
      : [...prefs.hidden, key];
    // Toujours garder au moins une colonne visible
    if (hidden.length >= columns.length) return;
    onChange({ ...prefs, hidden });
  };

  const hasCustomization = prefs.hidden.length > 0 || prefs.order.length > 0;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className={`inline-flex h-10 items-center gap-2 rounded-lg border px-3 text-sm font-semibold transition-colors ${
          isOpen || prefs.hidden.length > 0
            ? 'border-primary bg-primary/5 text-primary'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
        }`}
      >
        <Columns3 className="h-4 w-4" />
        Colonnes
        {prefs.hidden.length > 0 && (
          <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1 text-xs font-semibold text-white">
            {columns.length - prefs.hidden.length}/{columns.length}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Fermeture au clic extérieur */}
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 z-20 mt-2 w-64 rounded-xl border border-gray-200 bg-white p-2 shadow-xl">
            <p className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400">
              Colonnes affichées
            </p>
            <div className="max-h-72 overflow-y-auto">
              {columns.map((col) => (
                <label
                  key={col.key}
                  className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm text-gray-700 hover:bg-primary/5"
                >
                  <input
                    type="checkbox"
                    checked={!prefs.hidden.includes(col.key)}
                    onChange={() => toggleColumn(col.key)}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="flex-1 truncate">{col.label}</span>
                </label>
              ))}
            </div>
            {hasCustomization && (
              <button
                type="button"
                onClick={() => onChange({ hidden: [], order: [] })}
                className="mt-1 flex w-full items-center gap-1.5 rounded-lg border-t border-gray-100 px-2 py-2 text-xs font-medium text-gray-500 hover:text-gray-800"
              >
                <RotateCcw className="h-3 w-3" />
                Réinitialiser (ordre et visibilité)
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
